import React from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { Grid } from '@material-ui/core';
import { palette, AddBlue, UploadedFile, Typography, FileUploadButton, SimpleLoader } from '@dev/ee-components';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';

import { getGeneralInfo } from '@/store/account/actions';
import { formatBytes } from '@/services/tools/formatBytes';
import { useCampaignScreensPathsChecker } from '@/components/CampaignCreator/hooks/useCampaignScreensPathsChecker';
import {
    AddCampaignAttachment,
    getCampainCreatorStore,
    RemoveCampaignAttachment,
} from '@/store/campaignCreator/actions';
import { Divider } from '../../../styledComponents';

const AttachmentsWrapper = styled.div`
    margin-bottom: 32px;

    .MuiTypography-root {
        margin-left: 44px;
        margin-bottom: 32px;
    }

    .wrapperUploadedFile {
        padding: 24px;
    }

    .iconWrapperAddBlue {
        width: 24px;
        height: 24px;
        margin-top: 32px;

        .iconWrapperAddIcon {
            width: 16px;
            height: 16px;

            svg {
                width: 16px;
                height: 16px;
            }
        }
    }

    .MuiButton-contained {
        background: none;
        color: ${palette.indigo[6]};
        margin-top: 20px;
    }
    .MuiButton-contained:hover {
        background: none;
    }
`;

const UploadedFileWrapper = styled.div<{ isDetailsScreen: boolean }>`
    margin-bottom: 8px;
    ${({ isDetailsScreen }) =>
        !isDetailsScreen &&
        `
    margin-left: 44px;
    `}

    .wrapperUploadedFile {
        align-items: start;

        svg {
            width: 20px;
            height: 20px;
        }
    }
`;

const LoaderWrapper = styled.div`
    height: 70px;
    display: flex;
    margin-left: 44px;
    div:first-child {
        margin: auto;
    }
`;

const FileUploadButtonWrapper = styled.div`
    .iconWrapperAddBlue {
        margin-top: 0px;
    }
`;

export const TemplateAttachments = (): JSX.Element => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { campaign, activeTemplateIndex, attachmentLoading } = useSelector(getCampainCreatorStore);
    const generalInfo = useSelector(getGeneralInfo);
    const { isDetailsScreen } = useCampaignScreensPathsChecker();

    const campaignTemplate = campaign?.CampaignTemplates?.[activeTemplateIndex];
    const campaignAttachments = campaignTemplate?.Attachments;

    const emailSizeLimit = generalInfo?.EmailSizeLimit;

    const addAttachment = (event: React.ChangeEvent<HTMLInputElement>): void => {
        const file = event.target.files[0];
        dispatch(AddCampaignAttachment(file, activeTemplateIndex));
    };

    const deleteAttachment = (fileName): void => {
        dispatch(RemoveCampaignAttachment(fileName, activeTemplateIndex));
    };

    return (
        <>
            <Divider noMargin />
            {isDetailsScreen && (
                <Typography variant="h2" style={{ marginTop: 32, marginBottom: 24 }}>
                    {t('attachments')}
                </Typography>
            )}
            <AttachmentsWrapper>
                {!isDetailsScreen && (
                    <>
                        <Grid container direction="row">
                            <FileUploadButtonWrapper>
                                <FileUploadButton
                                    startIcon={<AddBlue />}
                                    onClick={addAttachment}
                                    text={t('addattachment')}
                                    id="campaign-creator"
                                    clearFileInput
                                />
                            </FileUploadButtonWrapper>
                        </Grid>
                        <Typography variant="body1" bodyVariant="onPaper">
                            <Trans
                                i18nKey="emailsizeinfo"
                                values={{
                                    size: emailSizeLimit,
                                }}
                            />
                        </Typography>
                    </>
                )}
                {campaignAttachments?.map((attachment, index) => {
                    const fileSize = formatBytes(attachment.Size);
                    const fileName = `${attachment.Name} (${fileSize})`;

                    return (
                        <UploadedFileWrapper key={`${attachment.Name}-${index}`} isDetailsScreen={isDetailsScreen}>
                            <UploadedFile
                                filename={fileName}
                                onDelete={() => deleteAttachment(attachment.Name)}
                                readOnly={isDetailsScreen}
                            />
                        </UploadedFileWrapper>
                    );
                })}
                {attachmentLoading && (
                    <LoaderWrapper>
                        <SimpleLoader color={palette.indigo[6]} />
                    </LoaderWrapper>
                )}
            </AttachmentsWrapper>
            {!isDetailsScreen && <Divider />}
        </>
    );
};
